import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, MapPin, TrendingUp, Users, Home, CheckCircle2 } from 'lucide-react';
import { navigateWithScroll } from '@/utils/navigation';

const CITIES_DATA = {
  geneve: {
    name: 'Genève',
    title: 'Gestion Airbnb à Genève',
    description: 'Optimisez votre bien à Genève avec Homely Conciergerie',
    hero: 'Genève, capitale internationale et destination prisée toute l\'année',
    why: 'Genève attire des voyageurs d\'affaires, touristes et diplomates tout au long de l\'année. La demande locative courte durée y est constante et élevée.',
    attractions: ['Jet d\'Eau', 'Vieille ville', 'Lac Léman', 'ONU', 'Quartier des banques'],
    profile: 'Clientèle internationale haut de gamme, voyageurs d\'affaires, touristes culturels',
    seasonality: 'Forte demande toute l\'année avec pics en été et périodes de congrès'
  },
  lausanne: {
    name: 'Lausanne',
    title: 'Gestion Airbnb à Lausanne',
    description: 'Maximisez vos revenus locatifs à Lausanne',
    hero: 'Lausanne, capitale olympique dynamique entre lac et montagnes',
    why: 'Lausanne combine tourisme, affaires et éducation. Le CIO, l\'EPFL et les nombreux événements garantissent une demande stable.',
    attractions: ['Musée Olympique', 'Cathédrale', 'Ouchy', 'EPFL', 'Vignobles Lavaux'],
    profile: 'Étudiants internationaux, sportifs, touristes, professionnels',
    seasonality: 'Demande régulière avec pics festival, rentrées universitaires et événements sportifs'
  },
  neuchatel: {
    name: 'Neuchâtel',
    title: 'Gestion Airbnb à Neuchâtel',
    description: 'Confiez-nous votre bien à Neuchâtel',
    hero: 'Neuchâtel, la perle du lac aux charmes authentiques',
    why: 'Ville universitaire et touristique, Neuchâtel séduit par son authenticité et sa proximité avec la nature.',
    attractions: ['Château', 'Vieille ville', 'Lac de Neuchâtel', 'Creux du Van', 'Vignobles'],
    profile: 'Touristes nature, étudiants, familles, visiteurs culturels',
    seasonality: 'Été très prisé, demande constante liée à l\'université'
  },
  montreux: {
    name: 'Montreux',
    title: 'Gestion Airbnb à Montreux',
    description: 'Optimisez votre location à Montreux',
    hero: 'Montreux, Riviera suisse et destination culturelle emblématique',
    why: 'Montreux attire une clientèle internationale premium grâce à son climat doux, ses festivals et son cadre d\'exception.',
    attractions: ['Montreux Jazz Festival', 'Château de Chillon', 'Promenade du lac', 'Rochers-de-Naye', 'Vignobles'],
    profile: 'Touristes haut de gamme, festivaliers, couples, retraités',
    seasonality: 'Pic en été (festival) et demande forte toute l\'année'
  },
  'crans-montana': {
    name: 'Crans-Montana',
    title: 'Gestion Airbnb à Crans-Montana',
    description: 'Valorisez votre chalet à Crans-Montana',
    hero: 'Crans-Montana, station alpine de prestige face aux Alpes valaisannes',
    why: 'Station haut de gamme prisée pour le ski, le golf et la randonnée. Clientèle internationale fortunée.',
    attractions: ['Domaine skiable', 'Golf 18 trous', 'Lac Grenon', 'Vue panoramique Alpes', 'Boutiques luxury'],
    profile: 'Skieurs haut de gamme, golfeurs, familles aisées, retraités actifs',
    seasonality: 'Double saison : hiver (ski) et été (golf, randonnée)'
  },
  verbier: {
    name: 'Verbier',
    title: 'Gestion Airbnb à Verbier',
    description: 'Maximisez vos revenus à Verbier',
    hero: 'Verbier, capitale suisse du freeride et destination internationale',
    why: 'Verbier est l\'une des stations les plus réputées au monde. Forte demande premium toute l\'année.',
    attractions: ['4 Vallées', 'Freeride', 'Après-ski', 'VTT', 'Festivals été'],
    profile: 'Skieurs experts, clientèle internationale fortunée, jeunes actifs',
    seasonality: 'Hiver très fort, été en croissance (VTT, randonnée)'
  },
  villars: {
    name: 'Villars',
    title: 'Gestion Airbnb à Villars',
    description: 'Confiez votre bien à Villars',
    hero: 'Villars, station familiale ensoleillée des Alpes vaudoises',
    why: 'Station familiale et accessible, proche de Genève et Lausanne. Clientèle fidèle et régulière.',
    attractions: ['Ski familial', 'Glacier 3000', 'Col de la Croix', 'Randonnées', 'Écoles internationales'],
    profile: 'Familles avec enfants, résidents secondaires, étudiants écoles internationales',
    seasonality: 'Hiver fort, vacances scolaires, demande été en hausse'
  }
};

export default function CityPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const city = CITIES_DATA[slug];
  
  const handleEstimateClick = () => {
    navigateWithScroll(navigate, '/estimation');
  };

  if (!city) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-[#2B2B2B] mb-4">Ville non trouvée</h1>
            <Button onClick={() => navigate('/')}>Retour à l'accueil</Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#F5F3EF] to-white" data-testid="city-hero">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <MapPin className="h-6 w-6 text-[#8A9474]" />
            <span className="text-[#8A9474] font-semibold">{city.name}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#2B2B2B] mb-6">
            {city.title}
          </h1>
          <p className="text-lg md:text-xl text-[#4A4A4A] mb-8 max-w-2xl mx-auto">
            {city.hero}
          </p>
          <Button 
            onClick={handleEstimateClick}
            size="lg"
            className="bg-[#8A9474] hover:bg-[#7A8464] text-white px-6 sm:px-10 py-4 sm:py-7 text-base sm:text-lg font-semibold rounded-lg transition-all transform hover:-translate-y-1 shadow-lg hover:shadow-xl"
            data-testid="city-cta-estimation"
          >
            Estimer mon bien à {city.name} <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Why Here */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-semibold text-[#2B2B2B] mb-6 text-center">
            Pourquoi Airbnb marche à {city.name}
          </h2>
          <p className="text-lg text-[#4A4A4A] mb-8 text-center max-w-3xl mx-auto">
            {city.why}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <Card className="border-2 border-[#E5E2DB] hover:border-[#8A9474] transition-all">
              <CardContent className="p-6 text-center">
                <TrendingUp className="h-10 w-10 text-[#8A9474] mx-auto mb-4" />
                <h3 className="font-semibold text-[#2B2B2B] mb-2">Forte demande</h3>
                <p className="text-sm text-[#4A4A4A]">{city.seasonality}</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-[#E5E2DB] hover:border-[#8A9474] transition-all">
              <CardContent className="p-6 text-center">
                <Users className="h-10 w-10 text-[#8A9474] mx-auto mb-4" />
                <h3 className="font-semibold text-[#2B2B2B] mb-2">Clientèle ciblée</h3>
                <p className="text-sm text-[#4A4A4A]">{city.profile}</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-[#E5E2DB] hover:border-[#8A9474] transition-all">
              <CardContent className="p-6 text-center">
                <Home className="h-10 w-10 text-[#8A9474] mx-auto mb-4" />
                <h3 className="font-semibold text-[#2B2B2B] mb-2">Optimisation locale</h3>
                <p className="text-sm text-[#4A4A4A]">Connaissance du marché {city.name}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Attractions */}
      <section className="py-20 px-6 bg-[#F5F3EF]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-semibold text-[#2B2B2B] mb-6 text-center">
            Attractivité de {city.name}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {city.attractions.map((attraction) => (
              <div 
                key={attraction}
                className="bg-white p-4 rounded-lg flex items-center gap-2"
              >
                <CheckCircle2 className="h-5 w-5 text-[#8A9474] flex-shrink-0" />
                <span className="text-sm text-[#2B2B2B]">{attraction}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-semibold text-[#2B2B2B] mb-6">
            Notre gestion à {city.name}
          </h2>
          <p className="text-lg text-[#4A4A4A] mb-12 max-w-2xl mx-auto">
            Nous optimisons votre bien pour maximiser vos revenus tout en assurant une gestion complète et professionnelle.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {[
              'Pricing dynamique adapté au marché local',
              'Communication 7j/7 en plusieurs langues',
              'Ménage professionnel entre chaque séjour',
              'Check-in/out personnalisés',
              'Maintenance réactive sur place',
              'Optimisation continue du taux d\'occupation'
            ].map((service) => (
              <div key={service} className="flex items-start gap-3 text-left bg-[#F5F3EF] p-4 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-[#8A9474] mt-0.5 flex-shrink-0" />
                <span className="text-sm text-[#2B2B2B]">{service}</span>
              </div>
            ))}
          </div>

          <Button 
            onClick={handleEstimateClick}
            size="lg"
            className="bg-[#8A9474] hover:bg-[#7A8464] text-white px-6 sm:px-10 py-4 sm:py-7 text-base sm:text-lg font-semibold"
            data-testid="city-services-cta"
          >
            Estimer mon bien <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 px-6 bg-gradient-to-r from-[#8A9474] to-[#7A8464] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-semibold mb-6">
            Prêt à optimiser votre bien à {city.name} ?
          </h2>
          <p className="text-lg mb-8 opacity-90">
            Découvrez combien vous pourriez générer avec une gestion professionnelle
          </p>
          <Button 
            onClick={handleEstimateClick}
            size="lg"
            className="bg-white text-[#8A9474] hover:bg-gray-100 px-6 sm:px-10 py-4 sm:py-7 text-base sm:text-lg font-semibold shadow-xl"
            data-testid="city-final-cta"
          >
            Estimer mon bien <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    </Layout>
  );
}
